import { useMemo, useState } from "react";
import { Box, Typography, IconButton, Collapse } from "@mui/material";
import Chart from "react-apexcharts";
import { ApexOptions } from "apexcharts";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

export default function ModelPreview({ baselineDailySummaryData, manualModelDetails, energyModel = "cooling" }: any) {
  const [isExpanded, setIsExpanded] = useState(true);

  const scatterData = useMemo(() => {
    if (!baselineDailySummaryData?.data) return [];
    if (energyModel === "cooling") return baselineDailySummaryData?.data?.map((item: any) => [item?.avg_outside_temp, item?.energy_usage_kwh]);
    else return baselineDailySummaryData?.data?.map((item: any) => [item?.avg_outside_temp, item?.heat_energy_usage_therms]);
  }, [baselineDailySummaryData]);

  const baselineLineData: [number, number][] = (() => {
    const points: [number, number][] = [];
    if (energyModel === "cooling") {
      for (let T = 0; T <= 100; T++) {
        let y: number;
        if (T <= manualModelDetails?.tccp) {
          y = manualModelDetails?.cc;
        } else {
          y = manualModelDetails?.cc + manualModelDetails?.bc * (T - manualModelDetails?.tccp);
        }
        points.push([T, y]);
      }
    } else {
      for (let T = -20; T <= 80; T++) {
        let y: number;
        if (T >= manualModelDetails?.thcp) {
          y = manualModelDetails?.ch;
        } else {
          y = manualModelDetails?.ch + manualModelDetails?.bh * (manualModelDetails?.thcp - T);
        }
        points.push([T, y]);
      }
    }
    return points;
  })();

  const series = [
    {
      name: "Scatter Dots",
      type: "scatter",
      data: scatterData,
    },
    {
      name: "Line",
      type: "line",
      data: baselineLineData,
    },
  ];

  const options: ApexOptions = {
    chart: {
      height: 350,
      type: "line",
      toolbar: {
        show: false,
      },
      zoom: { enabled: false },
    },
    stroke: {
      width: [0, 2],
    },
    markers: {
      size: [5, 0],
    },
    xaxis: {
      type: "numeric",
      tickAmount: 10,
      title: {
        text: "T = Avg Daily OSA Temp",
      },
    },
    yaxis: {
      title: {
        text: energyModel === "cooling" ? "E = Avg Daily Electric Power (kW)" : "Eh = Avg Daily Gas Rate (Therm/Hr)",
      },
      labels: {
        formatter: (val) => val.toFixed(2),
      },
    },
    tooltip: {
      shared: false,
      intersect: false,
    },
    legend: {
      show: false,
      position: "top",
    },
  };

  const handleToggleExpand = () => setIsExpanded((prev) => !prev);

  // console.log("Baseline Daily Summary scatter Data:", scatterData);
  // console.log("Baseline Daily Summary manualModelDetails:", manualModelDetails);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
        <Typography variant="h6">Model Preview</Typography>
        <IconButton onClick={handleToggleExpand} size="small">
          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ gap: 2, marginTop: 2, marginBottom: 2 }}>
          <Chart
            key={`${manualModelDetails?.cc}-${manualModelDetails?.bc}-${manualModelDetails?.tccp}-${manualModelDetails?.ch}-${manualModelDetails?.bh}-${manualModelDetails?.thcp}`}
            options={options}
            series={series}
            type="line"
            height={350}
          />
        </Box>
      </Collapse>
    </Box>
  );
}
